import { FilterArea, useFilterAreaContext } from "#src/components/FilterArea";
import {
  useGenerateWorkerDropdownOptions,
  useListWorkers,
} from "#src/components/hooks/adapters/useWorkers";
import {
  DropdownInput,
  PillToggleGroup,
  PillToggleVariants,
  type StorageKeys,
} from "@validereinc/common-components";
import { WORKER_RUN_STATUS_VALUES } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";
import { WorkerRunTableFilterSchemaKeys } from "./WorkerRunsFilterAreas.helpers";

/** Normalizes status filter from storage (may be array or string) for pill selection. */
const normalizeStatusFilter = (
  status: string | string[] | undefined
): string[] => {
  if (status == null) return [];
  return Array.isArray(status) ? status : [status];
};

const WorkerRunStatusPillToggles = ({
  onChange,
}: {
  onChange: (val: string[]) => void;
}) => {
  const { storedFilters } = useFilterAreaContext<{
    status?: string | string[];
  }>();
  const statusArr = normalizeStatusFilter(storedFilters.status);
  const isAllSelected = statusArr.length === 0 || statusArr.includes("");
  const pillToggles = [
    {
      name: "All",
      label: "All",
      value: "",
      isSelected: isAllSelected,
      shouldSelectAll: true,
    },
    ...WORKER_RUN_STATUS_VALUES.map((status) => ({
      name: status,
      label: toTitleCaseString(status, { intent: "ui" }),
      value: status,
      variant:
        status === "started"
          ? PillToggleVariants.PENDING
          : status === "completed"
            ? PillToggleVariants.GOOD
            : PillToggleVariants.FAILURE,
      isSelected: statusArr.includes(status),
    })),
  ];

  return (
    <PillToggleGroup
      name="status"
      pills={pillToggles}
      onChange={(val) => onChange(val)}
    />
  );
};

/**
 * Status filter chips rendered next to the Run table panel title.
 */
export const WorkerRunTableTitleDecorationFilterArea = ({
  filterConfigStorageKey,
}: Pick<StorageKeys, "filterConfigStorageKey">) => {
  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container aria-label="Filters for Worker Run">
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div style={{ marginRight: 8, display: "flex", gap: 8 }}>
              <WorkerRunStatusPillToggles
                onChange={(val) =>
                  handleOnChange(val, WorkerRunTableFilterSchemaKeys.status)
                }
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

export const WorkerRunTableFilterArea = ({
  filterConfigStorageKey,
}: Pick<StorageKeys, "filterConfigStorageKey">) => {
  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container aria-label="Filters for Worker Run">
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <WorkerRunTableFilterAreaContent handleOnChange={handleOnChange} />
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

const WorkerRunTableFilterAreaContent = ({
  handleOnChange,
}: {
  handleOnChange: (value: unknown, name: string) => void;
}) => {
  const { data: workersData } = useListWorkers({});
  const workers = workersData?.data ?? [];
  const workerOptions = useGenerateWorkerDropdownOptions(workers);

  return (
    <div style={{ display: "flex", gap: 8, marginRight: 8, marginBottom: 0 }}>
      <DropdownInput
        name={WorkerRunTableFilterSchemaKeys.workerId}
        isClearable
        isInline
        labelKey="label"
        valueKey="value"
        placeholder="Select worker..."
        options={workerOptions}
        isSortedAlphabetically
        description="Filter by worker"
        onChange={(val) => {
          handleOnChange(
            !val ? undefined : val,
            WorkerRunTableFilterSchemaKeys.workerId
          );
        }}
      />
    </div>
  );
};
